import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Form, SpaceBetween } from '@cloudscape-design/components'

import AccountNetworkApprovalPanel from './AccountNetworkApprovalPanel'
import { vpcAvailabilityZonesShowAzC } from '../../helper/accountRequestNetworkSummary'
import { buildNetworkApprovalFormValues } from '../../helper/accountNetworkApprovalPrefill'
import {
	buildAccountNetworkDetailsPayload,
	validateAccountNetworkDetails
} from '../../helper/accountNetworkValidation'
import useStgApproveAccount from '../../hooks/mutate/useStgApproveAccount'
import type { Account } from '../../types/account'

/** Maps account env type (Sandbox / Production / …) to GitLab member-accounts folder env slug. */
function envTypeForGitLabMemberAccountsPath(accountType: string | undefined): string {
	const t = String(accountType ?? '')
		.trim()
		.toLowerCase()
	if (t === 'production') return 'prod'
	if (t === 'sandbox') return 'sandbox'
	if (t === 'non-production' || t === 'nonproduction') return 'nonprod'
	return t.replace(/\s+/g, '-') || 'stg'
}

type Props = {
	account: Account
	thisUserEmail: string
	onApproved: () => void
	onApiError: (message: string) => void
	onRejectClick: () => void
	onCancel: () => void
}

const StgNetworkApprovalSection: FC<Props> = ({
	account,
	thisUserEmail,
	onApproved,
	onApiError,
	onRejectClick,
	onCancel
}) => {
	const accountRequestID = account.accountRequestID
	const { mutate: approveStg, isLoading: isApproving } =
		useStgApproveAccount(accountRequestID)

	const [formValues, setFormValuesState] = useState<Record<string, unknown>>({})
	const [isNetworkValid, setIsNetworkValid] = useState(false)
	const [networkPrefillGeneration, setNetworkPrefillGeneration] = useState(0)

	const stgDetails = useMemo(
		() => (account.stgRequest?.details ?? {}) as Record<string, unknown>,
		[account.stgRequest?.details]
	)

	const detailsFingerprint = useMemo(() => {
		const systemAlias = account.tags?.details?.systemAlias
		return JSON.stringify({
			stgDetails,
			accountType: account.type,
			systemAlias: systemAlias !== undefined ? systemAlias : undefined
		})
	}, [account.type, account.tags?.details?.systemAlias, stgDetails])

	useEffect(() => {
		const systemAlias = account.tags?.details?.systemAlias
		const parentDetails = (account.details ?? {}) as Record<string, unknown>
		const distributionList = String(
			parentDetails.distributionList ?? account.accountRequestID ?? ''
		).trim()
		setFormValuesState(
			buildNetworkApprovalFormValues(stgDetails, account.type, {
				onlyPrefillWhenNetworkPresent: true,
				systemAlias,
				distributionList,
				isStgFlow: true
			})
		)
		setNetworkPrefillGeneration((generation) => generation + 1)
	}, [account.accountRequestID, account.details, account.tags?.details?.systemAlias, account.type, detailsFingerprint, stgDetails])

	const setFormValues = useCallback((fieldName: string, value: unknown) => {
		setFormValuesState((prev) => ({ ...prev, [fieldName]: value }))
	}, [])

	// AZ C now follows the zone list the reviewer sees, not the parent account type — the STG
	// flow starts at two zones and may add a third.
	const isProduction = vpcAvailabilityZonesShowAzC(
		formValues.vpcAvailabilityZones,
		account.type
	)

	const handleApprove = () => {
		if (!isNetworkValid) return
		if (!validateAccountNetworkDetails(formValues, isProduction)) return

		const networkPayload = buildAccountNetworkDetailsPayload(formValues, isProduction)
		const approvalDetails = {
			...JSON.parse(JSON.stringify(formValues)),
			...networkPayload,
			accountWorkloadType: String(
				formValues.accountWorkloadType ?? stgDetails.accountWorkloadType ?? 'Non-EKS'
			),
			env_type: envTypeForGitLabMemberAccountsPath(account.type)
		} as Record<string, unknown>

		const rawSso = account.details?.SSOUserNames
		if (Array.isArray(rawSso)) {
			approvalDetails.SSOUserNames = rawSso.map(
				(user: { displayName?: string; userName: string }) => ({
					userName: user.userName ?? '',
					displayName: (user.displayName ?? '').trim()
				})
			)
		} else if (typeof rawSso === 'string' && rawSso.trim()) {
			approvalDetails.SSOUserNames = rawSso.split(',').map((s) => ({
				userName: s.trim(),
				displayName: ''
			}))
		} else {
			approvalDetails.SSOUserNames = []
		}

		approveStg(
			{
				action: 'stg_approve',
				accountRequestID,
				approverID: thisUserEmail,
				details: approvalDetails
			},
			{
				onSuccess: () => onApproved(),
				onError: (e: unknown) =>
					onApiError(e instanceof Error ? e.message : String(e))
			}
		)
	}

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="link" disabled={isApproving} onClick={onCancel}>
						Cancel
					</Button>
					<Button disabled={isApproving} onClick={onRejectClick}>
						Reject…
					</Button>
					<Button
						variant="primary"
						loading={isApproving}
						disabled={!isNetworkValid}
						onClick={handleApprove}
					>
						Approve
					</Button>
				</SpaceBetween>
			}
		>
			<AccountNetworkApprovalPanel
				formValues={formValues}
				setFormValues={setFormValues}
				isProduction={isProduction}
				accountType={account.type}
				onValidityChange={setIsNetworkValid}
				networkHeaderVariant="h2"
				networkPrefillGeneration={networkPrefillGeneration}
				allowThirdAzToggle
			/>
		</Form>
	)
}

export default StgNetworkApprovalSection
